import React, { useState } from 'react';
import { Card, Divider, Tag, Skeleton, message, notification } from 'antd';
import { ErrorRender } from '@partsbadger/library';
import { ThreeDViewerModal, viewerPartInfoFromQuotePart } from '../../../../components/ThreeDViewerModal';
import QuoteItemInformation from '../../../../components/Quotes/QuoteItemInformation';
import QuoteStore from '../../../../stores/QuoteStore';
import type { QuoteLineItemProps, QuoteItemQuantitiesProps, QuoteProps } from '../../../../types/types';
import { RenderStandardLineItem } from '../RenderStandardLineItem';
import { PartPreview, QuotedQuantity } from '../../../../components/Quotes';
import { useHistory } from 'react-router-dom';

type Props = {
    quote: QuoteProps | null;
    firstPart: QuoteLineItemProps;
    items: QuoteLineItemProps[];

    // state setters/utilities from parent
    setQuote: (q: QuoteProps) => void;
    loadLineItems: (quoteId: number) => Promise<void>;
    setQuoteItem: (item: QuoteLineItemProps) => void;
    setShowAllDynamicQuantitiesByItem: (id: number | null) => void;
    setShowRequestNewQuote: (v: boolean) => void;
    loadQuote: (quoteId: number) => Promise<void>;
    setLoading: (value: boolean) => void;
};

export const LineItemsGroup: React.FC<Props> = ({
    quote,
    firstPart,
    items,
    setQuote,
    loadLineItems,
    setQuoteItem,
    setShowAllDynamicQuantitiesByItem,
    setShowRequestNewQuote,
    loadQuote,
    setLoading,
}) => {
    if (!quote?.id) return <Skeleton />;

    const [loadingPriceOptions, setLoadingPriceOptions] = useState<boolean>(false);

    const history = useHistory();

    const handleRemove = (_item: QuoteLineItemProps, _item_dynamic?: QuoteItemQuantitiesProps) => {
        // setLoading(true);
        // setQuoteLineItems(QuoteStore.getQuoteLineItems);
        if (_item_dynamic) {
            QuoteStore.deleteDynamicVariation(_item.quote, _item_dynamic.id).then(() => loadQuote(quote?.id));
        } else {
            QuoteStore.deleteLineItem(_item.quote, { id: _item.id }).then(() => loadQuote(quote?.id));
        }
    };

    const handleUpdateItemPrice = async (priceId: string, lineItemId: number) => {
        setLoadingPriceOptions(true);
        const payload = {
            id: lineItemId,
            price_id: priceId,
        };
        await QuoteStore.updateItemPrice(quote?.id, payload)
            .then(async (data: QuoteLineItemProps) => {
                // The price endpoint only returns the line item, so the quote totals
                // have to be refetched for the summary to stay in sync.
                await loadQuote(quote.id);
                setLoadingPriceOptions(false);
            })
            .catch(error => {
                setLoadingPriceOptions(false);

                notification.error({
                    message: 'Error',
                    description: <ErrorRender error={error} />,
                });
            });
    };

    const sendToRequote = async (id: number | undefined) => {
        if (id) {
            setLoading(true);
            QuoteStore.requote(id)
                .then(response => {
                    history.replace(`/my-account/quotes/get-quote/?uid=${response.uid}`);
                })
                .catch(error => {
                    message.error('Something went wrong!');
                })
                .finally(() => setLoading(false));
        }
    };

    return (
        <Card className="shadow-sm" bodyStyle={{ paddingTop: '0.5rem' }}>
            <div className="d-flex flex-row align-items-center mb-1">
                <div className="mr-4">
                    {firstPart.product.attachments.file3d?.file_3djs ? (
                        <ThreeDViewerModal
                            url={firstPart.product.attachments.file3d.file_3djs}
                            partInfo={viewerPartInfoFromQuotePart(firstPart)}
                        >
                            <PartPreview
                                thumbnail={firstPart.product?.attachments?.file3d?.image}
                                has_file_3d={firstPart?.product?.has_file_3d}
                                width="85px"
                            />
                        </ThreeDViewerModal>
                    ) : (
                        <PartPreview
                            thumbnail={firstPart.product?.attachments?.file3d?.image}
                            has_file_3d={firstPart?.product?.has_file_3d}
                            width="85px"
                        />
                    )}
                </div>
                <div className="font-size-md font-weight-bold w-100">
                    {firstPart.name}
                    {firstPart.product.itar ? (
                        <Tag color="#9fbf91" style={{ fontSize: '0.75em' }} className="ml-2">
                            ITAR
                        </Tag>
                    ) : null}
                </div>
            </div>

            <div className="d-flex flex-row w-100">
                <div className="d-flex flex-column w-100">
                    {items.map((_item: QuoteLineItemProps, index: number) => {
                        return (
                            <>
                                <div key={_item.id} className="d-flex flex-md-row flex-column justify-between">
                                    <div className="mb-3">
                                        <QuoteItemInformation
                                            manufacturing_type={_item.manufacturing_type}
                                            material_name={_item.product.material}
                                            finish_name={_item.product.finish_related?.name}
                                            coating_name={_item.product.coating_related?.name}
                                            remove_coating={true}
                                            deviation_note={_item.product.deviation_note}
                                            handleUpdateProduct={async () => {
                                                await QuoteStore.updateLineItem(quote.id, {
                                                    quantity: _item.quantity,
                                                    coatings: [],
                                                    quote_has_product: _item.id,
                                                    deviation: true,
                                                    deviation_note: 'Coating removed by rep or customer',
                                                })
                                                    .then(response => {
                                                        setQuote(response.quote);
                                                        loadLineItems(_item.quote);
                                                        message.success('Coatings removed!');
                                                    })
                                                    .catch((error: any) => {
                                                        notification.error({
                                                            duration: 10,
                                                            key: 'coating-removed-error',
                                                            message: 'Error',
                                                            description: error.response.data['message'],
                                                        });
                                                    });
                                            }}
                                        />
                                    </div>

                                    <div className="flex-grow-1 mb-3">
                                        {_item.has_dynamic_quantities ? (
                                            _item.quote_item_quantities
                                                .filter(item => item.displayed)
                                                .map((_quantity_item, _quantity_item_index) => {
                                                    return (
                                                        <div key={_quantity_item.quantity}>
                                                            <QuotedQuantity
                                                                showTitles={_quantity_item_index == 0}
                                                                editable={true}
                                                                max_quantity={_item.quantity_max}
                                                                quantity={_quantity_item.quantity}
                                                                unit_price={parseFloat(_quantity_item.unit_price)}
                                                                total_price={parseFloat(_quantity_item.total_price)}
                                                                lead_time={_quantity_item.lead_time}
                                                                handleChangeQuantity={async _qty => {
                                                                    const response = await QuoteStore.updateLineItem(
                                                                        quote.id,
                                                                        {
                                                                            quote_has_product: _item.id,
                                                                            quote_line_item_quantity: _quantity_item.id,
                                                                            quantity: _qty,
                                                                        }
                                                                    );
                                                                    await QuoteStore.update_prices_line_item(
                                                                        _item.id,
                                                                        response.prices[0],
                                                                        _quantity_item.id
                                                                    );
                                                                    setQuote(response.quote);
                                                                    return response.prices[0];
                                                                }}
                                                                handleRemove={async () => {
                                                                    QuoteStore.remove_line_items(_item, _quantity_item);
                                                                    handleRemove(_item, _quantity_item);
                                                                }}
                                                            />
                                                        </div>
                                                    );
                                                })
                                        ) : (
                                            <RenderStandardLineItem
                                                key={_item.id}
                                                item={_item}
                                                onRemove={handleRemove}
                                                onChangePrice={handleUpdateItemPrice}
                                                onRequote={sendToRequote}
                                                setQuoteItem={setQuoteItem}
                                                setShowRequestNewQuote={setShowRequestNewQuote}
                                                setQuote={setQuote}
                                                quoteId={quote?.id}
                                            />
                                        )}
                                    </div>
                                </div>

                                {_item.has_dynamic_quantities && (
                                    <div className="flex flex-row w-full justify-center items-center">
                                        <a
                                            className="link underline text-red-600"
                                            onClick={() => {
                                                setQuoteItem(_item);
                                                setShowAllDynamicQuantitiesByItem(_item.id);
                                            }}
                                        >
                                            View Price Breaks
                                        </a>
                                    </div>
                                )}

                                {index !== items.length - 1 ? <Divider type={'horizontal'} /> : null}
                            </>
                        );
                    })}
                </div>
            </div>
        </Card>
    );
};
